import { motion } from "framer-motion";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { User, SignOut, SquaresFour, ChartLineUp, Rocket, ChartBar, UsersThree, DeviceMobile, ShareNetwork, Crown } from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import SessionActivityDialog from "@/components/dashboard/SessionActivityDialog";
import PricingDialog from "@/components/dashboard/PricingDialog";
import { HOME, PORTFOLIO, GROWTH_TEAM, PUBLISH_HISTORY, DASHBOARD } from "@/constants/testIds";

const PLAN_LABELS = { basic: "Basic", pro: "Pro", agency: "Agency" };

const Navbar = ({ onTryDemo }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <motion.nav
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-30 flex items-center justify-between px-6 lg:px-12 py-5 glass-panel border-b border-white/5"
    >
      <div
        data-testid={HOME.navLogo}
        onClick={() => navigate("/")}
        className="flex items-center gap-3 cursor-pointer"
      >
        <img src="/logo.jpg" alt="Skilmark" className="w-12 h-12 rounded-lg object-cover" />
        <div className="flex flex-col leading-tight">
          <span className="font-display font-extrabold text-lg tracking-tight">
            SKIL<span className="text-[#7C93B8]">MARK</span>
          </span>
          <span className="text-[10px] text-white/40 uppercase tracking-[0.15em]">Deploy Growth in Seconds</span>
        </div>
      </div>

      <div className="flex items-center gap-6">
        <Link to="/blog" data-testid="home-nav-blog-link" className="text-sm font-semibold text-white/70 hover:text-white transition-colors duration-300 hidden sm:inline">
          Blog
        </Link>

      {user ? (
        <div className="flex items-center gap-3">
          <button
            data-testid={DASHBOARD.planBadge}
            onClick={() => setPricingOpen(true)}
            title="Manage your plan"
            className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#F97316]/30 text-[#F97316] hover:bg-[#F97316]/10 transition-colors duration-300"
          >
            <Crown size={12} weight="fill" /> {PLAN_LABELS[user.plan] || "Basic"}
          </button>
          <NotificationBell />
          <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={HOME.navUserMenu} className="flex items-center gap-2 rounded-full bg-white/5 border border-white/10 pl-1.5 pr-4 py-1.5 hover:border-white transition-colors duration-300">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <span className="w-7 h-7 rounded-full bg-[#F97316] flex items-center justify-center">
                  <User size={14} weight="bold" />
                </span>
              )}
              <span className="text-sm font-medium">{user.name}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#0F0F0F] border-white/10 text-white">
            <DropdownMenuItem data-testid={HOME.navDashboardLink} onClick={() => navigate("/dashboard")} className="cursor-pointer focus:bg-white/10">
              <SquaresFour size={16} className="mr-2" /> My Portfolio
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="home-nav-growth-lead-link" onClick={() => navigate("/growth-lead")} className="cursor-pointer focus:bg-white/10">
              <Rocket size={16} className="mr-2" /> Growth Lead
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={GROWTH_TEAM.navLink} onClick={() => navigate("/growth-team")} className="cursor-pointer focus:bg-white/10">
              <UsersThree size={16} className="mr-2" /> Growth Team
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={PORTFOLIO.navLink} onClick={() => navigate("/portfolio-budget")} className="cursor-pointer focus:bg-white/10">
              <ChartBar size={16} className="mr-2" /> Portfolio Budget
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={PUBLISH_HISTORY.navLink} onClick={() => navigate("/publish-history")} className="cursor-pointer focus:bg-white/10">
              <ShareNetwork size={16} className="mr-2" /> Publish History
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={HOME.navSessionsBtn} onClick={() => setSessionsOpen(true)} className="cursor-pointer focus:bg-white/10">
              <DeviceMobile size={16} className="mr-2" /> Active Sessions
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={DASHBOARD.upgradePlanBtn} onClick={() => setPricingOpen(true)} className="cursor-pointer focus:bg-white/10">
              <Crown size={16} className="mr-2" /> Upgrade Plan
            </DropdownMenuItem>
            {user.is_admin && (
              <DropdownMenuItem data-testid={HOME.navAdminLink} onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-white/10">
                <ChartLineUp size={16} className="mr-2" /> Admin Analytics
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem data-testid={HOME.navLogoutBtn} onClick={handleLogout} className="cursor-pointer focus:bg-white/10 text-red-400">
              <SignOut size={16} className="mr-2" /> Log Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <button
            data-testid={HOME.navLoginBtn}
            onClick={() => navigate("/login")}
            className="text-sm font-semibold text-white/70 hover:text-white transition-colors duration-300"
          >
            Log In
          </button>
          <button
            data-testid={HOME.navTryDemo}
            onClick={onTryDemo}
            className="rounded-full bg-[#2563EB] text-white text-sm font-semibold px-5 py-2 hover:bg-[#3B82F6] transition-colors duration-300"
          >
            Try the Engine
          </button>
        </div>
      )}
      </div>
      <SessionActivityDialog open={sessionsOpen} onOpenChange={setSessionsOpen} onCurrentSessionRevoked={handleLogout} />
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </motion.nav>
  );
};

export default Navbar;
